import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { round } from 'lodash';
import { flatMap, map, take } from 'rxjs/operators';
import { DutyLogHttpClient } from 'src/app/shared/flight-log-api/duty-logs/duty-log-client.service';
import { AppState } from 'src/store';
import {
  DutyLogActionTypes,
  DutyLogEdited,
  DutyOff,
  DutyOn,
} from 'src/store/duty-log/duty-log.actions';
import { DutyLogEntity } from 'src/store/duty-log/duty-log.model';
import {
  selectDutyLogById,
  selectShiftByStartTimeApprox,
} from 'src/store/duty-log/duty-log.selectors';
import { sleep } from 'src/util/async';
import { backoffMs } from 'src/util/promise';

@Injectable()
export class DutyLogEffects {
  constructor(
    private actions$: Actions,
    private _dutyLogHttpClient: DutyLogHttpClient,
    private _store: Store<AppState>,
  ) {}

  @Effect({ dispatch: false })
  public whenDutyLogEditedFetch = this.actions$.pipe(
    ofType<DutyOff | DutyLogEdited>(DutyLogActionTypes.DutyOff, DutyLogActionTypes.DutyLogEdited),
    flatMap(async ({ payload: { id } }) => {
      const updatedApprox = new Date();
      const start = performance.now();
      let updatedDutyLog: DutyLogEntity | undefined;
      const maxAttempts = 10;
      let attemptCount = 0;
      while (attemptCount <= maxAttempts && updatedDutyLog == null) {
        await sleep(backoffMs(attemptCount));
        await this._fetchById(id);
        updatedDutyLog = await this._trySelectByIdAndUpdatedTimeApprox(id, updatedApprox);
        attemptCount++;
      }
      const duration = round(performance.now() - start);

      if (updatedDutyLog == null) {
        // tslint:disable-next-line: no-console
        console.warn(
          `Unable to fetch updated duty log after ${duration}ms and ${attemptCount} attempts.`,
        );
      } else {
        // tslint:disable-next-line:no-console
        console.info(`Fetched updated duty log after ${duration}ms and ${attemptCount} attempts.`);
      }
    }),
  );

  @Effect({ dispatch: false })
  public whenDutyOnFetchLog$ = this.actions$.pipe(
    ofType<DutyOn>(DutyLogActionTypes.DutyOn),
    flatMap(async ({ payload: { employeeId, dutyPeriodStartTime } }) => {
      const start = performance.now();
      let activeDutyLog: DutyLogEntity | undefined;
      const maxAttempts = 10;
      let attemptCount = 0;
      while (attemptCount <= maxAttempts && activeDutyLog == null) {
        await this._fetchActiveLog(employeeId);
        await sleep(backoffMs(attemptCount));
        activeDutyLog = await this._trySelectActiveLogByStartTimeApprox(
          employeeId,
          dutyPeriodStartTime,
        );
        attemptCount++;
      }
      const duration = round(performance.now() - start);
      if (activeDutyLog == null) {
        // tslint:disable-next-line: no-console
        console.warn(
          `Unable to fetch active duty log after ${duration}ms and ${attemptCount} attempts.`,
        );
      } else {
        // tslint:disable-next-line:no-console
        console.info(`Fetched active duty log after ${duration}ms and ${attemptCount} attempts.`);
      }
    }),
  );

  private _trySelectActiveLogByStartTimeApprox(employeeId: string, startTime: Date) {
    return this._store
      .select(selectShiftByStartTimeApprox(employeeId, startTime))
      .pipe(take(1))
      .toPromise();
  }

  private _trySelectByIdAndUpdatedTimeApprox(id: string, updated: Date) {
    return this._store
      .select(selectDutyLogById(id))
      .pipe(
        map((dl) => {
          if (dl != null && dl.updatedAt != null) {
            // tslint:disable-next-line: no-non-null-assertion
            const diff = Math.abs(dl.updatedAt!.getTime() - updated.getTime());
            const withinSkew = diff < 1000;
            return withinSkew ? dl : undefined;
          }
          return undefined;
        }),
        take(1),
      )
      .toPromise();
  }

  private _fetchById(id: string) {
    return this._dutyLogHttpClient.getById(id).toPromise();
  }

  private _fetchActiveLog(employeeId: string) {
    return this._dutyLogHttpClient
      .getMany({ pilotEmployeeId: employeeId, active: true })
      .toPromise();
  }
}
